import React, { useCallback, useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { ResponsiveDrawerForm } from "../../../components/ResponsiveDrawerForm";
import Quiz from "./Quiz";
import { API, get } from "../../../config";
import MainBody from "../../../components/MainBody/MainBody";
import { Badge } from "../../../components/ui/badge";
import { CheckCircle, XCircle } from "lucide-react";
import { useOrganization } from "../../../lib/context/OrganisationContext";
import { toast } from "../../../components/ui/use-toast";

const Training = () => {
  const [showQuiz, setShowQuiz] = useState(false);

  const [questionData, setQuestionData] = useState();
  const [courses, setCourses] = useState();
  const [assessment, setAssessment] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [endQuiz, setEndQuiz] = useState(false);
  const [assessmentQuiz, setAssessmentQuiz] = useState(false);
  const { organizationData } = useOrganization();

  const handleDownload = async (export_report_path) => {
    try {
      if (export_report_path) {
        var a = document.createElement("a");
        a.href = export_report_path;
        var file = export_report_path.split("/");
        a.setAttribute("download", file[file.length - 1] || "file.pdf");
        a.setAttribute("target", "_blank");
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
    } catch (e) {
      const errors = e.response?.data?.errors;
      toast({
        variant: "destructive",
        description: errors,
        duration: 3000,
      });
    }
  };

  const toggleShowQuizModal = (assessment) => {
    if (showQuiz && endQuiz) {
      fetchTrainings();
    }
    if (showQuiz) {
      setAssessmentQuiz(false);
    }
    if (assessment) {
      setAssessmentQuiz(true);
    } else {
      setAssessmentQuiz(false);
    }
    setShowQuiz(!showQuiz);
  };

  const fetchTrainings = useCallback(async () => {
    if (organizationData?.organizationId) {
      try {
        setIsLoading(true);
        const { data } = await get(
          `${API.GET_TRAININGS}?organization_id=${
            organizationData?.organizationId
              ? organizationData?.organizationId
              : ""
          }`,
        );
        setCourses(data?.data);
        setAssessment(data?.assessment);

        setIsLoading(false);
      } catch (e) {
        setCourses([]);
        setIsLoading(false);
      }
    }
  }, [organizationData?.organizationId]);

  useEffect(() => {
    fetchTrainings();
  }, [fetchTrainings]);

  return (
    <MainBody
      search={false}
      actionContent={
        <>
          {assessment?.questions?.length > 0 && (
            <div className="flex items-center justify-between gap-4">
              {/* LEFT STATUS + SCORE */}
              <div className="flex items-center gap-3">
                {/* STATUS BADGE */}
                {assessment?.quiz_result?.status === 1 && (
                  <Badge
                    variant="success"
                    className="flex items-center gap-1 px-3 py-1"
                  >
                    <CheckCircle className="h-4 w-4" />
                    <span>Completed</span>

                    {assessment?.quiz_result?.quiz_date_time && (
                      <span className="ml-2 text-xs font-normal opacity-80">
                        {assessment.quiz_result.quiz_date_time.split(" ")[0]}
                      </span>
                    )}
                  </Badge>
                )}

                {assessment?.quiz_result?.status === 0 && (
                  <Badge
                    variant="destructive"
                    className="flex items-center gap-1 px-3 py-1"
                  >
                    <XCircle className="h-4 w-4" />
                    <span>Failed</span>

                    {assessment?.quiz_result?.quiz_date_time && (
                      <span className="ml-2 text-xs font-normal opacity-80">
                        {assessment.quiz_result.quiz_date_time.split(" ")[0]}
                      </span>
                    )}
                  </Badge>
                )}

                {/* SCORE BADGE */}
                {assessment?.quiz_result?.score !== undefined && (
                  <Badge variant="secondary" className="px-3 py-1">
                    Assessment Score: {assessment.quiz_result.score}%
                  </Badge>
                )}
              </div>

              {/* ACTION BUTTON */}
              <Button
                onClick={() => {
                  setAssessmentQuiz(true);
                  setQuestionData(assessment?.questions);
                  toggleShowQuizModal();
                }}
              >
                {assessment?.quiz_result
                  ? "Retake Assessment"
                  : "Start Assessment"}
              </Button>
              {assessment?.quiz_result?.certificate && (
                <Button
                  onClick={() => {
                    handleDownload(assessment?.quiz_result?.certificate);
                  }}
                >
                  {"Download Assessment"}
                </Button>
              )}
            </div>
          )}
        </>
      }
      actionAlign={"end"}
      loading={isLoading}
      innerBody={true}
      parentHeight={true}
      mainClassName="w-[calc(100%-3rem)] "
    >
      <div className="sm:min-h-[640px]">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
          {courses?.map((course) => (
            <Card
              key={course.id}
              className="flex flex-col shadow-md rounded-xl overflow-hidden"
            >
              <CardHeader className="flex p-0 flex-col gap-2 min-h-[140px] relative space-y-0">
                {/* TOP LEFT: STATUS BADGE */}
                {course?.quiz_result?.status === 1 && (
                  <Badge
                    variant="success"
                    className="absolute left-2 top-2 z-10 flex items-center gap-1 px-2 py-1 text-xs"
                  >
                    <CheckCircle className="h-4 w-4" />
                    Completed
                    {course.quiz_result.quiz_date_time && (
                      <span className="ml-1 opacity-80">
                        {course.quiz_result.quiz_date_time.split(" ")[0]}
                      </span>
                    )}
                  </Badge>
                )}

                {course?.quiz_result?.status === 0 && (
                  <Badge
                    variant="destructive"
                    className="absolute left-2 top-2 z-10 flex items-center gap-1 px-2 py-1 text-xs"
                  >
                    <XCircle className="h-4 w-4" />
                    Failed
                    {course.quiz_result.quiz_date_time && (
                      <span className="ml-1 opacity-80">
                        {course.quiz_result.quiz_date_time.split(" ")[0]}
                      </span>
                    )}
                  </Badge>
                )}

                {/* TOP RIGHT: SCORE BADGE */}
                {course?.quiz_result?.score !== undefined && (
                  <Badge
                    variant="secondary"
                    className="absolute right-2 top-2 z-10 px-2 py-1 text-xs"
                  >
                    Score: {course.quiz_result.score}%
                  </Badge>
                )}

                {/* BANNER IMAGE */}
                <img
                  src={course.banner_image}
                  alt={course?.subtitle}
                  className="object-contain"
                />
              </CardHeader>
              <CardContent className="flex-1 flex flex-col gap-2 p-4">
                <h4 className="font-semibold">{course.title}</h4>
                <p className="text-sm text-muted-foreground">
                  {course.description}
                </p>
              </CardContent>
              <CardFooter className="p-4 pt-1 flex flex-col w-full gap-3">
                <a
                  href={course.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full"
                >
                  <Button variant="outline" className="w-full">
                    Click to enter this course
                  </Button>
                </a>
                <Button
                  className="w-full"
                  onClick={() => {
                    setQuestionData(course?.questions);
                    toggleShowQuizModal();
                  }}
                >
                  Start Quiz
                </Button>
              </CardFooter>
            </Card>
          ))}
          {assessment && (
            <>
              <Card
                key={assessment.id}
                className="flex flex-col shadow-md rounded-xl overflow-hidden"
              >
                <CardHeader className="flex p-0 flex-col gap-2 min-h-[140px] relative space-y-0">
                  {/* TOP LEFT: STATUS BADGE */}
                  {assessment?.quiz_result?.status === 1 && (
                    <Badge
                      variant="success"
                      className="absolute left-2 top-2 z-10 flex items-center gap-1 px-2 py-1 text-xs"
                    >
                      <CheckCircle className="h-4 w-4" />
                      Completed
                      {assessment.quiz_result.quiz_date_time && (
                        <span className="ml-1 opacity-80">
                          {assessment.quiz_result.quiz_date_time.split(" ")[0]}
                        </span>
                      )}
                    </Badge>
                  )}

                  {assessment?.quiz_result?.status === 0 && (
                    <Badge
                      variant="destructive"
                      className="absolute left-2 top-2 z-10 flex items-center gap-1 px-2 py-1 text-xs"
                    >
                      <XCircle className="h-4 w-4" />
                      Failed
                      {assessment.quiz_result.quiz_date_time && (
                        <span className="ml-1 opacity-80">
                          {assessment.quiz_result.quiz_date_time.split(" ")[0]}
                        </span>
                      )}
                    </Badge>
                  )}

                  {/* TOP RIGHT: SCORE BADGE */}
                  {assessment?.quiz_result?.score !== undefined && (
                    <Badge
                      variant="secondary"
                      className="absolute right-2 top-2 z-10 px-2 py-1 text-xs"
                    >
                      Score: {assessment.quiz_result.score}%
                    </Badge>
                  )}

                  {/* BANNER IMAGE */}
                  <img
                    src={assessment.banner_image}
                    alt={assessment?.subtitle}
                    className="object-contain"
                  />
                </CardHeader>
                <CardContent className="flex-1 flex flex-col gap-2 p-4">
                  <h4 className="font-semibold">{assessment.title}</h4>
                  <p className="text-sm text-muted-foreground">
                    {assessment.description}
                  </p>
                </CardContent>
                <CardFooter className="p-4 pt-1 flex flex-col w-full gap-3">
                  <Button
                    className="w-full"
                    onClick={() => {
                      setAssessmentQuiz(true);
                      setQuestionData(assessment?.questions);
                      toggleShowQuizModal(assessment);
                    }}
                  >
                    Start Video
                  </Button>
                </CardFooter>
              </Card>
            </>
          )}
        </div>

        <div className="mt-3">
          Copyright © 2025 Commonwealth of Australia - AUSTRAC. All Rights
          Reserved.
        </div>
        {showQuiz && (
          <ResponsiveDrawerForm
            description={""}
            title={assessmentQuiz ? "Assessment Quiz" : "Quiz"}
            handleOpen={() => {}}
            handleClose={() => {
              setShowQuiz(false);
            }}
            className={"md:w-[65vw]"}
            open={showQuiz}
            buttonClassName={""}
            setOpen={setShowQuiz}
            read={true}
          >
            <Quiz
              toggleShowQuizModal={toggleShowQuizModal}
              questionData={questionData}
              setEndQuiz={setEndQuiz}
              assessmentQuiz={assessmentQuiz}
              assessment={assessment}
            />
          </ResponsiveDrawerForm>
        )}
      </div>
    </MainBody>
  );
};

export default Training;
